import * as React from 'react'
import {
  Button,
  IconButton,
  Typography,
  TextField,
  Grid,
  Box,
  InputAdornment,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material'
import { VisibilityOff, Visibility } from '@mui/icons-material'
import { useForm, Controller, SubmitHandler } from 'react-hook-form'
import { grey } from '@mui/material/colors'
import { useTranslation } from 'react-i18next'
import { SnackbarProvider, useSnackbar } from 'notistack'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { Link as RouterLink, useLocation } from 'react-router-dom'

import { PASSWORD_REGEX } from 'helpers/constants'
import UserService from 'api/services/User'
import usePageView from 'hooks/usePageView'

interface PasswordFormFields {
  password: string
  confirmationPassword: string
}

interface State {
  showOldPassword: boolean
  showPassword: boolean
  showConfirmPassword: boolean
}

export default function PasswordUpdate() {
  usePageView()
  const { t } = useTranslation()

  const { executeRecaptcha } = useGoogleReCaptcha()

  const createGoogleToken = React.useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }
    return await executeRecaptcha()
  }, [executeRecaptcha])

  const location = useLocation()
  const query = new URLSearchParams(location.search)

  const token = query.get('token') ?? ''

  const [local, setLocal] = React.useState<State>({
    showOldPassword: false,
    showPassword: false,
    showConfirmPassword: false,
  })

  const handleClickShowPassword = () => {
    setLocal({
      ...local,
      showPassword: !local.showPassword,
    })
  }

  const handleClickShowConfirmPassword = () => {
    setLocal({
      ...local,
      showConfirmPassword: !local.showConfirmPassword,
    })
  }

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
  }

  const defaultValues = {
    password: '',
    confirmationPassword: '',
  }

  const { handleSubmit, control, reset, getValues } =
    useForm<PasswordFormFields>({
      defaultValues,
    })

  const { enqueueSnackbar } = useSnackbar()

  const resetPasswordMutation = UserService.hooks.useResetPassword({
    onSuccess: () => {
      reset()
    },
    onError: (err) => {
      enqueueSnackbar(t('tables.responses.error') as unknown as string, {
        variant: 'error',
      })
    },
  })

  const onSubmit: SubmitHandler<PasswordFormFields> = async (data) => {
    const recaptchaToken = (await createGoogleToken()) ?? ''
    resetPasswordMutation.mutate({
      password: data.password,
      confirmationPassword: data.confirmationPassword,
      token,
      recaptchaToken,
    })
  }

  return (
    <SnackbarProvider>
      <Box
        sx={{
          mt: 4,
          bgcolor: 'background.paper',
          px: { xs: 2, sm: 2, md: 3, lg: 4, xl: 6 },
          boxShadow: 25,
          border: 1,
          borderColor: grey[300],
          pt: 1,
          pb: 4,
          borderRadius: 2.5,
          maxWidth: (theme) => theme.breakpoints.values.sm,
          // mx: 'auto',
          py: 5,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Typography
            variant="h5"
            color="grey.500"
            align="center"
            sx={{ fontWeight: 'bolder' }}
          >
            {t('dashboard.pages.password.formTitle')}
          </Typography>
          <Grid container justifyContent="center" spacing={2} sx={{ py: 5 }}>
            {resetPasswordMutation.isSuccess ? (
              <>
                <Grid item xs={12}>
                  <Alert severity="success">
                    <Typography
                      display="block"
                      variant="body1"
                      color="text.secondary"
                    >
                      {t(`dashboard.pages.password.success`)}
                    </Typography>
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Link component={RouterLink} to="/login" underline="none">
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{ textTransform: 'none', px: 4, py: 1, fontSize: 25 }}
                    >
                      {t('resetPasswordPage.login')}
                    </Button>
                  </Link>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <Controller
                        name={`password`}
                        control={control}
                        rules={{
                          required: t(
                            'validation.fieldRequired'
                          ) as unknown as string,
                          pattern: {
                            value: PASSWORD_REGEX,
                            message: t('error.password'),
                          },
                        }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            fullWidth
                            type={local.showPassword ? 'text' : 'password'}
                            variant="filled"
                            label={t(
                              'dashboard.pages.password.newPasswordLabel'
                            )}
                            size="small"
                            {...field}
                            error={!!error}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    color={
                                      local.showPassword ? 'primary' : 'default'
                                    }
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                  >
                                    {local.showPassword ? (
                                      <VisibilityOff />
                                    ) : (
                                      <Visibility />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            helperText={error ? error?.message : ' '}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name={`confirmationPassword`}
                        control={control}
                        rules={{
                          required: t(
                            'validation.fieldRequired'
                          ) as unknown as string,
                          pattern: {
                            value: PASSWORD_REGEX,
                            message: t('error.password'),
                          },
                          validate: {
                            matchesPreviousPassword: (value) => {
                              const { password } = getValues()
                              return (
                                password === value ||
                                (t(
                                  'dashboard.pages.password.diffrentPassword'
                                ) as unknown as string)
                              )
                            },
                          },
                        }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            fullWidth
                            type={
                              local.showConfirmPassword ? 'text' : 'password'
                            }
                            variant="filled"
                            label={t(
                              'dashboard.pages.password.confirmPasswordLabel'
                            )}
                            size="small"
                            {...field}
                            error={!!error}
                            helperText={error ? error?.message : ' '}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    color={
                                      local.showConfirmPassword
                                        ? 'primary'
                                        : 'default'
                                    }
                                    onClick={handleClickShowConfirmPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                  >
                                    {local.showConfirmPassword ? (
                                      <VisibilityOff />
                                    ) : (
                                      <Visibility />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={resetPasswordMutation.isLoading}
                    sx={{
                      px: 4,
                      py: 1,
                      fontSize: 25,
                      minHeight: 70,
                    }}
                  >
                    {resetPasswordMutation.isLoading ? (
                      <CircularProgress
                        sx={{ color: 'background.paper' }}
                        size={25}
                      />
                    ) : (
                      t('dashboard.pages.password.updateButton')
                    )}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </form>
      </Box>
    </SnackbarProvider>
  )
}
